
import { useState } from "react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { Eye, EyeOff, Lock } from "lucide-react";

// Schema para validação de senha
const passwordSchema = z.object({
  senha: z.string().min(6, "A senha deve ter pelo menos 6 caracteres"),
  confirmacaoSenha: z.string(),
}).refine(data => data.senha === data.confirmacaoSenha, {
  message: "As senhas não coincidem",
  path: ["confirmacaoSenha"],
});

interface PasswordFormProps {
  userId: string;
  onSuccess: () => void;
  onCancel: () => void;
}

export const PasswordForm = ({ userId, onSuccess, onCancel }: PasswordFormProps) => {
  const { changePassword } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSenha, setShowSenha] = useState(false);
  const [showConfirmacao, setShowConfirmacao] = useState(false);

  // Configuração do formulário
  const form = useForm({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      senha: "",
      confirmacaoSenha: "",
    },
  });

  // Função para salvar
  const onSubmit = async (values: z.infer<typeof passwordSchema>) => {
    setIsSubmitting(true);
    try {
      const success = await changePassword(userId, values.senha);
      if (success) {
        onSuccess();
      }
    } catch (error) {
      console.error("Erro ao alterar senha:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5 py-4">
        <FormField
          control={form.control}
          name="senha"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-gray-700">Nova Senha</FormLabel>
              <FormControl>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <button
                    type="button"
                    onClick={() => setShowSenha(!showSenha)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showSenha ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                  <Input
                    type={showSenha ? "text" : "password"}
                    placeholder="******"
                    className="pl-10 pr-10"
                    {...field}
                  />
                </div>
              </FormControl>
              <FormMessage className="text-xs mt-1" />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="confirmacaoSenha"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-gray-700">Confirmar Nova Senha</FormLabel>
              <FormControl>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <button
                    type="button"
                    onClick={() => setShowConfirmacao(!showConfirmacao)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showConfirmacao ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                  <Input
                    type={showConfirmacao ? "text" : "password"}
                    placeholder="******"
                    className="pl-10 pr-10"
                    {...field}
                  />
                </div>
              </FormControl>
              <FormMessage className="text-xs mt-1" />
            </FormItem>
          )}
        />

        <DialogFooter className="pt-2">
          <Button 
            type="button" 
            variant="outline" 
            onClick={onCancel}
            className="border-gray-300 text-gray-700"
          >
            Cancelar
          </Button>
          <Button 
            type="submit" 
            disabled={isSubmitting}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {isSubmitting ? "Salvando..." : "Alterar Senha"}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
};
